import React, { useMemo, useState, useEffect } from 'react';
import { Box, styled } from '@mui/system';
import NavBar from '../NavBar/NavBar';
import { getUserDetails, getCollegeName } from '../HomePage/userManagement';
import { Typography, Dialog } from '@mui/material';
import image5URL from '../images/image5';
import ViewResult from './ViewResult';
import EmailAvatar from '../HomePage/EmailAvatar';
import { ref, set } from 'firebase/database';
import { auth, db } from '../../firebase';
import { useLocation } from 'react-router-dom';

const MainContainer = styled('div')({
    height: "100vh",
    display: 'flex',
});

const FirstHalf = styled('div')({
    width: '20%',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    borderRight: '2.5px solid #D8D0D0',
    borderBottom: '2.5px solid #D8D0D0',
    background: '#fafafa',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
});

const ContentHalf = styled('div')({
    flex: 1,
    backgroundColor: 'white',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
});

const GenerateButton = styled('button')({
    marginTop: '20px',
    padding: '10px 26px',
    color: 'black',
    fontFamily: 'Poppins, sans-serif',
    fontWeight: 500,
    fontSize: '20px',
    borderRadius: '10px',
    cursor: 'pointer',
    '&:hover': {
        background: 'black',
        color: 'white',
        transform: 'scale(1.1)',
    },
});

const keys = [
    'Project Management', 'Design and Visualization', 'Negotiations and Deal Making',
    'Marketing and Promotions', 'Critical Thinking and Problem Solving', 'Cross Cultural Engagements',
    'External Interactions', 'Internal Interactions', 'Numerical and Quantitative Aptitude',
    'Resource Management', 'Analytics, Research and Insights', 'Creation and Innovation',
    'Subject Matter Expertise', 'Operations and Process Excellence', 'Recruitment and Support',
    'Administration', 'Self Management and Development', 'Dynamic Presence',
    'Social Initiatives and Impact', 'Monitoring, Inspecting and Testing', 'Leadership',
];

const dateStamp = new Date().toString();

const FinalOutput = () => {
    const location = useLocation();
    const { selectedKeys, selectedQuestionsWithHandled, totalWeight } = location.state || {};
    const [showDialog, setShowDialog] = useState(false);
    const routePaths = useMemo(() => [
        '/userdetails',
        '/dashboard',
        '/quetionairepart1',
    ], []);
    const activePageIndex = useMemo(() => routePaths.indexOf('/quetionairepart1'), [routePaths]);

    useEffect(() => {
        if (!selectedKeys || !selectedQuestionsWithHandled) {
            console.error("No data received from QuestionairePart2");
        }
    }, [selectedKeys, selectedQuestionsWithHandled]);

    const calculateCompetencyScores = (selectedQuestionsWithHandled) => {
        const competencyCategories = {
            "Relationship Management": ["External Interactions", "Internal Interactions", "Cross Cultural Engagements"],
            "Leadership & Influence": ["Leadership", "Dynamic Presence", "Critical Thinking and Problem Solving"],
            "Quantitative and Analytical": ["Numerical & Quantitative Aptitude", "Analytics, Research and Insights", "Monitoring, Inspecting and Testing"],
            "Strategic Thinking & Execution": ["Negotiations and Deal Making", "Resource Management", "Project Management"],
            "Innovation & Creativity": ["Creation and Innovation", "Design and Visualization", "Marketing and Promotions"],
            "Operational Excellence": ["Operations and Process Excellence", "Administration", "Recruitment and Support"],
            "Self Development & Ethical Leadership": ["Subject Matter Expertise", "Social Initiatives and Impact", "Self Management and Development"]
        };

        const competencyScores = {};
        let totalLeadershipScore = 0;

        Object.entries(competencyCategories).forEach(([competency, keys]) => {
            let totalScore = 0;
            let count = 0;

            keys.forEach(key => {
                if (selectedQuestionsWithHandled[key]) {
                    const score = selectedQuestionsWithHandled[key].score || 0;
                    totalScore += score;
                    totalLeadershipScore += score;
                    count++;
                }
            });

            competencyScores[competency] = count > 0 ? (totalScore / 15) * 100 : 0;
        });

        const leadershipScore = (totalLeadershipScore / 105 * 100);
        return { competencyScores, leadershipScore };
    };

    const { competencyScores, leadershipScore } = calculateCompetencyScores(selectedQuestionsWithHandled);

    const addResponsesToArray = async (userUid, calculatedFinal) => {
        const dateRef1 = ref(db, `${getCollegeName()}/Assessments/${userUid}/Assessment ${dateStamp}`);

        const cleanedResults = {
            responses: calculatedFinal.reduce((acc, item) => {
                acc[item.key] = {
                    question: item.question || "Question text not available",
                    score: item.score || 0,
                    normalizedScore: item.normalizedScore || 0,
                };
                return acc;
            }, {}),
            leadershipScore,
            competencyScores
        };
        await set(dateRef1, cleanedResults);
    };

    const missingElements = useMemo(() => {
        return keys.filter(
            (key) => !selectedKeys.some(
                (selectedKey) => selectedKey.trim().toLowerCase() === key.trim().toLowerCase()
            )
        );
    }, [selectedKeys]);

    const {
        final: calculatedFinal,
        normalizedFinalTotal,
        activityFinalTotal,
        rawFinalTotal,
        enjoymentPerct,
        outcomePercent,
        activityCount,
    } = useMemo(() => {
        let finalResults = [];
        let activityFinalTotals = [];
        let enjoymentSum = 0;
        let outcomeSum = 0;
        let totalActivities = 0;

        Object.entries(selectedQuestionsWithHandled).forEach(([key, question]) => {
            let totalActivityScore = 0;
            let numActivities = question.activities.length;

            question.activities.forEach((activity) => {
                const activityScore = activity.handled * activity.weight;
                totalActivityScore += activityScore;
                enjoymentSum += activity.handled * activity.enjoyment;
                outcomeSum += activity.outcome;
                totalActivities++;
            });

            finalResults.push({
                key,
                question: question.question,
                score: totalActivityScore,
                normalizedScore: totalActivityScore / totalWeight,
            });

            activityFinalTotals.push(totalActivityScore / numActivities);
        });

        const normalizedFinalTotal = finalResults.map((result) => ({
            ...result,
            normalizedScore: result.score / totalWeight,
        }));

        const rawFinalTotal = finalResults.map((result) => result.score / totalWeight);

        const enjoymentPerct = (enjoymentSum / totalActivities) * 100;
        const outcomePercent = (outcomeSum / totalActivities) * 100;
        return {
            final: finalResults,
            normalizedFinalTotal,
            activityFinalTotal: activityFinalTotals,
            rawFinalTotal,
            enjoymentPerct,
            outcomePercent,
            activityCount: totalActivities,
        };
    }, [selectedQuestionsWithHandled]);

    const handleAddButtonClick = () => {
        const userUid = auth.currentUser.uid;
        addResponsesToArray(userUid, calculatedFinal, rawFinalTotal, normalizedFinalTotal, activityFinalTotal, enjoymentPerct, outcomePercent, activityCount);
        setShowDialog(true);
    };

    return (
        <MainContainer>
            <FirstHalf>
                <NavBar activePage={activePageIndex} user={getUserDetails} />
            </FirstHalf>
            <ContentHalf>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexDirection: 'row-reverse', padding: '10px', width: '90%' }}>
                    <EmailAvatar />
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', width: '80%', marginTop: '20px' }}>
                    <Typography
                        style={{
                            fontFamily: 'Poppins, sans-serif',
                            fontWeight: 700,
                            fontSize: '20px'
                        }}>
                        You have completed the assessment
                    </Typography>
                </div>
                <Box sx={{
                    border: '1px solid #D8D0D0',
                    display: 'flex', alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    padding: '10px', width: '80%',
                    borderRadius: '12px'
                }}>
                    <img src={image5URL} alt='FinalOutput img' style={{ width: '30%' }} />
                    <Typography
                        style={{
                            fontFamily: 'Poppins, sans-serif',
                            fontWeight: 600,
                            fontSize: '14px'
                        }}>
                        Congratulations! Your Strength Assessment Report is here
                    </Typography>
                    <br />
                    <Typography
                        style={{
                            fontFamily: 'Poppins, sans-serif',
                            fontWeight: 500,
                            fontSize: '14px'
                        }}>
                        Play to your strengths and pursue development needs for a successful and fulfilling career ahead. Best wishes!
                    </Typography>
                    <GenerateButton onClick={handleAddButtonClick}>
                        View Result
                    </GenerateButton>
                    <Dialog open={showDialog} onClose={() => setShowDialog(false)} PaperProps={{
                        style: {
                            width: '80%',
                            maxWidth: 'none',
                            position: 'absolute',
                            top: '45%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            padding: '20px',
                        },
                    }}>
                        <ViewResult
                            yo={normalizedFinalTotal}
                            missingElements={missingElements}
                            outcomePercent={outcomePercent}
                            competencyScores={competencyScores}
                            leadershipScore={leadershipScore}
                        />
                    </Dialog>
                </Box>
            </ContentHalf>
        </MainContainer>
    );
};

export default FinalOutput;
